<template>
  <section class="section">
    <div class="container">
      <h1 class="title is-4">📥 Peppol Inbox</h1>
      <p class="subtitle is-6 has-text-grey">
        List of incoming documents received via Peppol
      </p>

      <!-- Search bar and filters -->
      <div class="level mb-4">
        <div class="level-left">
          <div class="field has-addons">
            <div class="control has-icons-left">
              <input
                v-model="search"
                class="input"
                type="text"
                placeholder="Search by sender or invoice number..."
              />
              <span class="icon is-left">
                <i class="fas fa-search"></i>
              </span>
            </div>
            <div class="control">
              <button class="button is-light" @click="fetchInbox">
                Search
              </button>
            </div>
          </div>
        </div>

        <div class="level-right">
          <button
            class="button is-info is-light"
            @click="showFilters = !showFilters"
          >
            <i class="fas fa-filter"></i>&nbsp; Filters
          </button>
        </div>
      </div>

      <!-- Filter Drawer -->
      <transition name="slide-fade">
        <div v-if="showFilters" class="box filters-panel">
          <h2 class="subtitle is-5 mb-3">
            <i class="fas fa-sliders-h"></i> Filters
          </h2>

          <div class="columns">
            <!-- Document Type -->
            <div class="column">
              <h3 class="has-text-weight-semibold">Document Type</h3>
              <div v-for="t in docTypes" :key="t.value" class="field">
                <label class="checkbox">
                  <input
                    type="checkbox"
                    v-model="filters.types"
                    :value="t.value"
                  />
                  {{ t.label }}
                </label>
              </div>
            </div>

            <!-- Status -->
            <div class="column">
              <h3 class="has-text-weight-semibold">Status</h3>
              <div v-for="s in statuses" :key="s.value" class="field">
                <label class="checkbox">
                  <input
                    type="checkbox"
                    v-model="filters.statuses"
                    :value="s.value"
                  />
                  {{ s.label }}
                </label>
              </div>
            </div>
          </div>

          <div class="buttons mt-3">
            <button class="button is-success" @click="applyFilters">
              Apply Filters
            </button>
            <button class="button is-light" @click="resetFilters">Reset</button>
          </div>
        </div>
      </transition>

      <!-- Inbox Table -->
      <div class="table-container mt-4">
        <table class="table is-fullwidth is-hoverable">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  :checked="allSelected"
                  @change="toggleSelectAll"
                />
              </th>
              <th>#</th>
              <th>Type</th>
              <th>Status</th>
              <th>Invoice #</th>
              <th>Sender</th>
              <th>Issue Date</th>
              <th>Due Date</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="!filteredDocuments.length && !loading">
              <td colspan="7" class="has-text-centered has-text-grey">
                No incoming documents found.
              </td>
            </tr>

            <tr v-for="(doc, index) in filteredDocuments" :key="doc.id">
              <td>
                <input
                  type="checkbox"
                  :checked="isSelected(doc.id)"
                  @click.stop
                  @change="toggleSelection(doc.id)"
                />
              </td>
              <td>{{ index + 1 + (page - 1) * limit }}</td>
              <td>
                <span :class="['tag', typeClass(doc.type)]">
                  {{ doc.type }}
                </span>
              </td>
              <td>
                <span :class="['tag', statusClass(doc)]">
                  {{ statusLabel(doc) }}
                </span>
              </td>
              <td>
                {{
                  doc.parsed.invoiceNumber || doc.parsed.creditNoteNumber || '-'
                }}
              </td>
              <td>{{ doc.parsed.seller.name }}</td>
              <td>{{ formatDate(doc.parsed.issueDate) }}</td>
              <td>{{ formatDate(doc.parsed.dueDate) }}</td>
              <td>
                <button
                  class="button is-small is-info ml-2"
                  @click="openDetail(doc)"
                  title="Open document details"
                >
                  Details
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <nav class="pagination is-centered mt-4" v-if="totalPages > 1">
        <a
          class="pagination-previous"
          :disabled="page === 1"
          @click="changePage(page - 1)"
          >Previous</a
        >
        <a
          class="pagination-next"
          :disabled="page === totalPages"
          @click="changePage(page + 1)"
          >Next</a
        >
        <ul class="pagination-list">
          <li v-for="p in totalPages" :key="p">
            <a
              class="pagination-link"
              :class="{ 'is-current': p === page }"
              @click="changePage(p)"
              >{{ p }}</a
            >
          </li>
        </ul>
      </nav>

      <p class="has-text-grey mt-4">
        Showing {{ filteredDocuments.length }} of {{ total }} incoming documents
      </p>
      <div class="level mt-4">
        <div class="level-left">
          <p class="has-text-grey">
            {{ selectedRecommandDocumentIds.length }} document(s) selected
          </p>
        </div>

        <div class="level-right">
          <button
            class="button is-primary"
            :disabled="selectedRecommandDocumentIds.length === 0"
            @click="openSendModal"
            v-if="canExportXML"
          >
            📤 Transfer XMLs
          </button>
        </div>
      </div>
    </div>
    <div v-if="isSendModalOpen" class="modal is-active">
      <div class="modal-background" @click="isSendModalOpen = false"></div>

      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Transfer XML files to accountant</p>
          <button
            class="delete"
            aria-label="close"
            @click="isSendModalOpen = false"
          ></button>
        </header>

        <section class="modal-card-body">
          <div class="content">
            <p>Send selected XML files to your accountant.</p>
          </div>

          <!-- Recipient -->
          <div class="field">
            <label class="label">Recipient(s)</label>
            <div class="control">
              <input
                v-model="emailForm.to"
                class="input"
                type="text"
                :class="{ 'is-danger': emailError }"
                placeholder="accountant@email.com"
              />
            </div>
            <p class="help">Separate multiple emails with commas</p>
            <p v-if="emailError" class="help is-danger">
              Please enter at least one valid email
            </p>
          </div>

          <!-- Language (future-proof, même si backend pas encore utilisé) -->
          <div class="field">
            <label class="label">Language</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="emailForm.language">
                  <option value="en">English</option>
                  <option value="fr">French</option>
                  <option value="nl">Dutch</option>
                </select>
              </div>
            </div>
          </div>

          <article class="message is-info mt-4">
            <div class="message-body">
              XML files will be sent as a ZIP attachment.
            </div>
          </article>
        </section>

        <footer class="modal-card-foot">
          <button
            class="button"
            @click="isSendModalOpen = false"
            :disabled="sending"
          >
            Cancel
          </button>

          <button
            class="button is-primary"
            :class="{ 'is-loading': sending }"
            @click="sendXmls"
          >
            Send
          </button>
        </footer>
      </div>
    </div>
  </section>
  <div
    v-if="toast.show"
    class="notification"
    :class="toast.type"
    style="position: fixed; top: 1rem; right: 1rem; z-index: 50"
  >
    <button class="delete" @click="toast.show = false"></button>
    {{ toast.message }}
  </div>
</template>

<script>
import api from '@/utils/axios';

export default {
  name: 'PeppolInbox',
  data() {
    return {
      documents: [],
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 1,
      search: '',
      showFilters: false,
      filters: { types: [], statuses: [] },
      docTypes: [
        { label: 'Invoice', value: 'invoice' },
        { label: 'Credit Note', value: 'creditNote' },
      ],
      statuses: [
        { label: 'Read', value: 'read' },
        { label: 'Unread', value: 'unread' },
        { label: 'Failed', value: 'failed' },
      ],
      loading: false,
      selectedRecommandDocumentIds: [],
      isSendModalOpen: false,
      sending: false,
      emailForm: {
        to: '',
        language: 'en',
      },
      emailError: false,
      toast: {
        show: false,
        message: '',
        type: 'is-success',
      },
    };
  },
  computed: {
    filteredDocuments() {
      return this.documents
        .filter((doc) => {
          const type = (doc.type || '').toLowerCase();
          const status = (this.statusLabel(doc) || '').toLowerCase();

          const matchesType =
            this.filters.types.length === 0 ||
            this.filters.types.some((t) => t.toLowerCase() === type);

          const matchesStatus =
            this.filters.statuses.length === 0 ||
            this.filters.statuses.some((s) => status.includes(s.toLowerCase()));

          const matchesSearch =
            !this.search ||
            (doc.id || '').toLowerCase().includes(this.search.toLowerCase()) ||
            (doc.senderId || '')
              .toLowerCase()
              .includes(this.search.toLowerCase());

          return matchesType && matchesStatus && matchesSearch;
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
    allSelected() {
      return (
        this.filteredDocuments.length > 0 &&
        this.filteredDocuments.every((doc) =>
          this.selectedRecommandDocumentIds.includes(doc.id)
        )
      );
    },
    canExportXML() {
      return true;
    },
  },
  methods: {
    async fetchInbox() {
      this.loading = true;

      try {
        const response = await api.get('/peppol/inbox/', {
          params: {
            page: this.page,
            limit: this.limit,
            search: this.search,
          },
        });

        const data = response.data;

        this.documents = data.documents || [];
        this.total = data.total || 0;
        this.totalPages = data.totalPages || 1;
      } catch (error) {
        console.error('❌ Error fetching inbox documents:', error);
      } finally {
        this.loading = false;
      }
    },

    changePage(p) {
      if (p >= 1 && p <= this.totalPages) {
        this.page = p;
        this.fetchInbox(); // IMPORTANT
      }
    },

    applyFilters() {
      this.showFilters = false;
    },

    resetFilters() {
      this.filters.types = [];
      this.filters.statuses = [];
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('fr-BE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    async openDetail(doc) {
      try {
        // ✅ Si pas encore lu → mark as read
        if (!doc.readAt) {
          await api.post(`/peppol/documents/${doc.id}/mark-as-read/`, {
            read: true,
          });

          // 🔥 IMPORTANT : update local (optimistic)
          doc.readAt = new Date().toISOString();
        }

        // 👉 navigation
        this.$router.push({
          name: 'PeppolDocumentDetail',
          params: { id: doc.id },
        });
      } catch (error) {
        console.error('Error marking document as read:', error);
      }
    },
    // --- Compute readable status for inbox ---
    statusLabel(doc) {
      if (doc.readAt) return 'Read';
      if (doc.error || doc.failed) return 'Failed';
      return 'Unread';
    },

    // --- Compute badge color based on inbox status ---
    statusClass(doc) {
      const label = this.statusLabel(doc).toLowerCase();
      if (label.includes('read')) return 'is-info';
      if (label.includes('unread')) return 'is-warning';
      if (label.includes('fail')) return 'is-danger';
      return 'is-light';
    },

    // --- Color per document type ---
    typeClass(type) {
      const t = (type || '').toLowerCase();
      if (t === 'invoice') return 'is-success';
      if (t === 'creditnote') return 'is-link';
      return 'is-light';
    },
    isSelected(id) {
      return this.selectedRecommandDocumentIds.includes(id);
    },

    toggleSelection(id) {
      if (this.selectedRecommandDocumentIds.includes(id)) {
        this.selectedRecommandDocumentIds =
          this.selectedRecommandDocumentIds.filter((d) => d !== id);
      } else {
        this.selectedRecommandDocumentIds.push(id);
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedRecommandDocumentIds = [];
      } else {
        this.selectedRecommandDocumentIds = this.filteredDocuments.map(
          (doc) => doc.id
        );
      }
    },
    openSendModal() {
      this.isSendModalOpen = true;

      const org = this.$store.state.organization || {};
      const emails = org.accountant_emails || [];

      // 🔥 RESET COMPLET (clé du problème)
      this.emailForm = {
        to: emails.join(', '),
        language: 'en',
      };

      this.emailError = false;
    },
    async sendXmls() {
      this.emailError = false;

      // Split emails
      const emails = this.emailForm.to
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e);

      // Validate
      if (emails.length === 0 || !this.validateEmails(emails)) {
        this.emailError = true;
        return;
      }
      this.sending = true;

      try {
        await api.post('/peppol/send-xmls/', {
          documents: this.filteredDocuments
            .filter((doc) => this.selectedRecommandDocumentIds.includes(doc.id))
            .map((doc) => ({
              id: doc.id,
              number: doc.parsed?.invoiceNumber || doc.id,
            })),
          recipients: emails,
          language: this.emailForm.language,
        });

        this.showToast(
          'XML files successfully sent to the accountant.',
          'is-success'
        );

        this.selectedRecommandDocumentIds = [];
        this.isSendModalOpen = false;
      } catch (error) {
        console.error('❌ Error sending XML files:', error);

        this.showToast(
          'An error occurred while sending the XML files.',
          'is-danger'
        );
      } finally {
        this.sending = false;
      }
    },
    validateEmails(emails) {
      const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emails.every((email) => regex.test(email.trim()));
    },
    showToast(message, type = 'is-success') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;

      setTimeout(() => {
        this.toast.show = false;
      }, 4000);
    },
  },
  mounted() {
    this.fetchInbox();
  },
};
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.tag {
  text-transform: capitalize;
}

/* Animation for filter drawer */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.filters-panel {
  background-color: #fafafa;
  border: 1px solid #e5e5e5;
}

/* --- Tag colors override for readability --- */
.tag.is-success {
  background-color: #23d160 !important; /* green */
  color: white !important;
}
.tag.is-link {
  background-color: #3273dc !important; /* blue */
  color: white !important;
}
.tag.is-info {
  background-color: #209cee !important; /* light blue */
  color: white !important;
}
.tag.is-warning {
  background-color: #ffdd57 !important; /* yellow */
  color: #363636 !important;
}
.tag.is-danger {
  background-color: #ff3860 !important; /* red */
  color: white !important;
}
.tag.is-light {
  background-color: #f5f5f5 !important; /* grey */
  color: #4a4a4a !important;
}
</style>
